import { ActionIcon, Button, Input, Text, TextInput } from "@mantine/core";
import { Plus, Trash } from "lucide-react";

import { FieldLabel } from "../../ui/FieldLabel";
import classes from "./forms.module.css";

export interface ListRowsProps {
  label: string;
  description: string;
  info: string;
  placeholder: string;
  value: string[];
  onChange: (value: string[]) => void;
  /** The schema's limits: how many rows, and how long each. */
  max: number;
  itemMax: number;
  error?: string | undefined;
}

/**
 * A list field as the live product form draws it (design-system.md 3.6): numbered rows with a
 * remove button, and "Add Element" until the limit. A blank row is not sent.
 */
export function ListRows(props: ListRowsProps) {
  const { label, description, info, placeholder, value, onChange, max, itemMax, error } = props;
  return (
    <Input.Wrapper
      label={<FieldLabel label={label} info={info} spoken />}
      description={description}
      error={error}
      labelElement="div"
    >
      <div className={classes.list}>
        {value.map((item, index) => (
          // Rows have no id of their own; each is the item at that position.
          <div key={index} className={classes.listItem}>
            <Text component="span" className={classes.listIndex} aria-hidden>
              {index + 1}
            </Text>
            <TextInput
              className={classes.grow}
              aria-label={`${label} ${index + 1}`}
              placeholder={placeholder}
              maxLength={itemMax}
              value={item}
              onChange={(event) => {
                const next = event.currentTarget.value;
                onChange(value.map((each, at) => (at === index ? next : each)));
              }}
            />
            <ActionIcon
              size={36}
              variant="text"
              aria-label={`Remove ${label} ${index + 1}`}
              onClick={() => onChange(value.filter((_, at) => at !== index))}
            >
              <Trash size={16} strokeWidth={1.5} />
            </ActionIcon>
          </div>
        ))}
        <Button
          variant="text"
          className={classes.listAdd}
          leftSection={<Plus size={16} />}
          disabled={value.length >= max}
          aria-label={`Add ${label} element`}
          onClick={() => onChange([...value, ""])}
        >
          Add Element
        </Button>
      </div>
    </Input.Wrapper>
  );
}
